import { useEffect, useRef } from 'react';

// Performance monitoring utilities
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.observers = [];
    this.isSupported = 'PerformanceObserver' in window;
  }

  // Initialize performance monitoring
  init() {
    if (!this.isSupported) {
      console.warn('PerformanceObserver not supported');
      return;
    }

    this.observeNavigation();
    this.observePaint();
    this.observeLargestContentfulPaint();
    this.observeFirstInputDelay();
    this.observeCumulativeLayoutShift();
    this.observeResourceLoading();
  }

  // Observe navigation timing
  observeNavigation() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.recordMetric('navigation', {
            name: entry.name,
            duration: entry.duration,
            loadEventEnd: entry.loadEventEnd,
            domContentLoadedEventEnd: entry.domContentLoadedEventEnd,
            firstContentfulPaint: this.getFirstContentfulPaint(),
          });
        });
      });
      
      observer.observe({ entryTypes: ['navigation'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('Navigation timing observation failed:', error);
    }
  }

  // Observe paint timing
  observePaint() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.recordMetric('paint', {
            name: entry.name,
            startTime: entry.startTime,
          });
        });
      });
      
      observer.observe({ entryTypes: ['paint'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('Paint timing observation failed:', error);
    }
  }

  // Observe Largest Contentful Paint
  observeLargestContentfulPaint() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        this.recordMetric('lcp', {
          startTime: lastEntry.startTime,
          size: lastEntry.size,
          element: lastEntry.element?.tagName,
          url: lastEntry.url,
        });
      });
      
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('LCP observation failed:', error);
    }
  }

  // Observe First Input Delay
  observeFirstInputDelay() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.recordMetric('fid', {
            delay: entry.processingStart - entry.startTime,
            duration: entry.duration,
            name: entry.name,
          });
        });
      });
      
      observer.observe({ entryTypes: ['first-input'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('FID observation failed:', error);
    }
  }

  // Observe Cumulative Layout Shift
  observeCumulativeLayoutShift() {
    try {
      let clsValue = 0;
      
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        
        this.recordMetric('cls', {
          value: clsValue,
          entries: entries.length,
        });
      });
      
      observer.observe({ entryTypes: ['layout-shift'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('CLS observation failed:', error);
    }
  }

  // Observe resource loading
  observeResourceLoading() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name.includes('chunk') || entry.name.includes('.js') || entry.name.includes('.css')) {
            this.recordMetric('resource', {
              name: entry.name,
              duration: entry.duration,
              transferSize: entry.transferSize,
              encodedBodySize: entry.encodedBodySize,
              decodedBodySize: entry.decodedBodySize,
              initiatorType: entry.initiatorType,
            });
          }
        });
      });
      
      observer.observe({ entryTypes: ['resource'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('Resource timing observation failed:', error);
    }
  }

  // Record custom metrics
  recordMetric(type, data) {
    const timestamp = performance.now();
    
    if (!this.metrics.has(type)) {
      this.metrics.set(type, []);
    }
    
    this.metrics.get(type).push({
      ...data,
      timestamp,
    });

    // Trigger custom event for real-time monitoring
    window.dispatchEvent(new CustomEvent('performance-metric', {
      detail: { type, data: { ...data, timestamp } }
    }));
  }

  // Get specific metric
  getMetric(type) {
    return this.metrics.get(type) || [];
  }

  // Get all metrics
  getAllMetrics() {
    const result = {};
    this.metrics.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  // Get Web Vitals summary
  getWebVitals() {
    const navigation = this.getMetric('navigation')[0];
    const lcp = this.getMetric('lcp')[0];
    const fid = this.getMetric('fid')[0];
    const cls = this.getMetric('cls')[0];
    const fcp = this.getFirstContentfulPaint();

    return {
      // First Contentful Paint
      fcp: fcp?.startTime,
      fcpRating: this.rateFCP(fcp?.startTime),
      
      // Largest Contentful Paint
      lcp: lcp?.startTime,
      lcpRating: this.rateLCP(lcp?.startTime),
      
      // First Input Delay
      fid: fid?.delay,
      fidRating: this.rateFID(fid?.delay),
      
      // Cumulative Layout Shift
      cls: cls?.value,
      clsRating: this.rateCLS(cls?.value),
      
      // Time to Interactive (approximation)
      tti: navigation?.domContentLoadedEventEnd,
      
      // Total Page Load Time
      loadTime: navigation?.loadEventEnd,
    };
  }

  // Helper methods for ratings
  rateFCP(value) {
    if (!value) return 'unknown';
    if (value <= 1800) return 'good';
    if (value <= 3000) return 'needs-improvement';
    return 'poor';
  }

  rateLCP(value) {
    if (!value) return 'unknown';
    if (value <= 2500) return 'good';
    if (value <= 4000) return 'needs-improvement';
    return 'poor';
  }

  rateFID(value) {
    if (!value) return 'unknown';
    if (value <= 100) return 'good';
    if (value <= 300) return 'needs-improvement';
    return 'poor';
  }

  rateCLS(value) {
    if (!value) return 'unknown';
    if (value <= 0.1) return 'good';
    if (value <= 0.25) return 'needs-improvement';
    return 'poor';
  }

  // Get First Contentful Paint
  getFirstContentfulPaint() {
    const paintEntries = this.getMetric('paint');
    return paintEntries.find(entry => entry.name === 'first-contentful-paint');
  }

  // Bundle size analysis
  analyzeBundleSize() {
    const resources = this.getMetric('resource');
    const jsResources = resources.filter(r => r.name.includes('.js'));
    const cssResources = resources.filter(r => r.name.includes('.css'));
    
    const totalJSSize = jsResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
    const totalCSSSize = cssResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
    
    return {
      totalJSSize,
      totalCSSSize,
      totalSize: totalJSSize + totalCSSSize,
      jsCount: jsResources.length,
      cssCount: cssResources.length,
      largestJS: jsResources.reduce((max, r) => 
        (r.transferSize || 0) > (max.transferSize || 0) ? r : max, {}),
      slowestResource: resources.reduce((max, r) => 
        r.duration > (max.duration || 0) ? r : max, {}),
    };
  }

  // Performance recommendations
  getRecommendations() {
    const vitals = this.getWebVitals();
    const bundle = this.analyzeBundleSize();
    const recommendations = [];

    // FCP recommendations
    if (vitals.fcpRating === 'poor') {
      recommendations.push({
        type: 'fcp',
        severity: 'high',
        message: 'First Contentful Paint is slow. Consider optimizing critical rendering path.',
        solutions: [
          'Minimize render-blocking resources',
          'Optimize font loading',
          'Use efficient CSS selectors',
          'Implement code splitting'
        ]
      });
    }

    // LCP recommendations
    if (vitals.lcpRating === 'poor') {
      recommendations.push({
        type: 'lcp',
        severity: 'high',
        message: 'Largest Contentful Paint is slow. Optimize your largest content element.',
        solutions: [
          'Optimize images and media',
          'Implement lazy loading',
          'Use efficient image formats (WebP, AVIF)',
          'Optimize server response times'
        ]
      });
    }

    // FID recommendations
    if (vitals.fidRating === 'poor') {
      recommendations.push({
        type: 'fid',
        severity: 'medium',
        message: 'First Input Delay is high. Reduce JavaScript execution time.',
        solutions: [
          'Split long tasks',
          'Optimize third-party scripts',
          'Use web workers for heavy computations',
          'Implement code splitting'
        ]
      });
    }

    // CLS recommendations
    if (vitals.clsRating === 'poor') {
      recommendations.push({
        type: 'cls',
        severity: 'medium',
        message: 'Cumulative Layout Shift is high. Reduce visual instability.',
        solutions: [
          'Include size attributes on images and videos',
          'Reserve space for ads and embeds',
          'Avoid inserting content above existing content',
          'Use transform animations instead of layout animations'
        ]
      });
    }

    // Bundle size recommendations
    if (bundle.totalJSSize > 500000) { // 500KB
      recommendations.push({
        type: 'bundle',
        severity: bundle.totalJSSize > 1000000 ? 'high' : 'medium',
        message: 'JavaScript bundle size is large. Consider optimizing.',
        solutions: [
          'Implement code splitting',
          'Tree shake unused code',
          'Use dynamic imports',
          'Analyze bundle with webpack-bundle-analyzer'
        ]
      });
    }

    return recommendations;
  }

  // Export performance data
  exportData() {
    return {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      connection: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      } : null,
      metrics: this.getAllMetrics(),
      webVitals: this.getWebVitals(),
      bundleAnalysis: this.analyzeBundleSize(),
      recommendations: this.getRecommendations(),
    };
  }

  // Send data to analytics service
  async sendToAnalytics(endpoint = '/api/performance') {
    try {
      const data = this.exportData();
      
      if ('sendBeacon' in navigator) {
        navigator.sendBeacon(endpoint, JSON.stringify(data));
      } else {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
      }
    } catch (error) {
      console.warn('Failed to send performance data:', error);
    }
  }

  // Cleanup observers
  disconnect() {
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (error) {
        console.warn('Failed to disconnect observer:', error);
      }
    });
    this.observers = [];
  }
}


export const usePerformanceMonitor = () => {
  const monitorRef = useRef(null);

  useEffect(() => {
    monitorRef.current = new PerformanceMonitor();
    monitorRef.current.init();

    // Send data when page is about to unload
    const handleBeforeUnload = () => {
      monitorRef.current?.sendToAnalytics();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      monitorRef.current?.disconnect();
    };
  }, []);

  return monitorRef.current;
};

// Component performance tracking
export const trackComponentPerformance = (componentName) => {
  const startTime = performance.now();
  
  return {
    finish: () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Record component performance
      window.dispatchEvent(new CustomEvent('component-performance', {
        detail: {
          componentName,
          duration,
          startTime,
          endTime,
        }
      }));
      
      return duration;
    }
  };
};

// HOC for component performance tracking
export const withPerformanceTracking = (Component) => {
  const WrappedComponent = (props) => {
    const componentName = Component.displayName || Component.name || 'Unknown';
    
    useEffect(() => {
      const tracker = trackComponentPerformance(componentName);
      return () => tracker.finish();
    }, [componentName]);

    return <Component {...props} />;
  };
  
  WrappedComponent.displayName = `withPerformanceTracking(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();